import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { HiDownload, HiMenuAlt4, HiX } from "react-icons/hi";
import { FiArrowUpRight, FiMoon, FiSun } from "react-icons/fi";
import { motion, AnimatePresence, useScroll, useSpring } from "framer-motion";

import { client } from "../../client";
import "./Navbar.scss";

const LINKS = [
  { id: "home", label: "Home" },
  { id: "about", label: "Process" },
  { id: "work", label: "Projects" },
  { id: "studio", label: "Studio" },
  { id: "skills", label: "Capabilities" },
  { id: "contact", label: "Contact" },
];

const NAV_SCROLL_OFFSET = 88;

const easeOutCubic = (value) => 1 - Math.pow(1 - value, 3);

const Navbar = ({ theme, onToggleTheme }) => {
  const [toggle, setToggle] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [active, setActive] = useState("home");
  const [cvUrl, setCvUrl] = useState(null);
  const scrollFrame = useRef(0);
  const isProgrammaticScroll = useRef(false);

  const { scrollYProgress } = useScroll();
  const progress = useSpring(scrollYProgress, { stiffness: 180, damping: 32, mass: 0.4 });

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    const sections = LINKS.map(({ id }) => document.getElementById(id)).filter(Boolean);
    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((entry) => entry.isIntersecting)
          .sort((a, b) => b.intersectionRatio - a.intersectionRatio)[0];

        if (!isProgrammaticScroll.current && visible?.target?.id) {
          setActive(visible.target.id);
        }
      },
      { rootMargin: "-25% 0px -55% 0px", threshold: [0.12, 0.3, 0.55] }
    );

    sections.forEach((section) => observer.observe(section));
    return () => observer.disconnect();
  }, []);

  useEffect(() => () => cancelAnimationFrame(scrollFrame.current), []);

  useEffect(() => {
    document.body.classList.toggle("nav-open", toggle);
    return () => document.body.classList.remove("nav-open");
  }, [toggle]);

  useEffect(() => {
    client
      .fetch('*[_type == "resume"][0]{ file { asset->{ url } } }')
      .then((data) => {
        if (data?.file?.asset?.url) setCvUrl(data.file.asset.url);
      })
      .catch(console.warn);
  }, []);

  const scrollTo = (event, id) => {
    event.preventDefault();
    const el = document.getElementById(id);
    if (!el) return;

    cancelAnimationFrame(scrollFrame.current);

    const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    const targetY =
      id === "home"
        ? 0
        : Math.max(0, el.getBoundingClientRect().top + window.scrollY - NAV_SCROLL_OFFSET);

    window.history.replaceState(null, "", window.location.pathname);
    setActive(id);
    isProgrammaticScroll.current = true;

    if (reduceMotion) {
      window.scrollTo(0, targetY);
      isProgrammaticScroll.current = false;
      return;
    }

    const startY = window.scrollY;
    const distance = targetY - startY;
    const duration = Math.min(900, Math.max(420, Math.abs(distance) * 0.28));
    const startTime = performance.now();

    const tick = (now) => {
      const progressValue = Math.min((now - startTime) / duration, 1);
      const nextY = startY + distance * easeOutCubic(progressValue);
      window.scrollTo(0, nextY);

      if (progressValue < 1) {
        scrollFrame.current = requestAnimationFrame(tick);
        return;
      }

      window.scrollTo(0, targetY);
      window.setTimeout(() => {
        isProgrammaticScroll.current = false;
      }, 120);
    };

    scrollFrame.current = requestAnimationFrame(tick);
  };

  const brand = (
    <>
      <svg
        className="nav__mark"
        viewBox="0 0 18 20"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        aria-hidden="true"
      >
        <path
          d="M2.5 1.5 L2.5 16.5 L7 12.6 L9.6 18.5 L12.4 17.2 L9.8 11.4 L15.5 10.8 Z"
          fill="currentColor"
        />
      </svg>
      <span className="nav__word">
        Usama&nbsp;Sajid
        <small>Frontend Engineer</small>
      </span>
    </>
  );

  const isDarkTheme = theme === "dark";
  const themeToggleLabel = isDarkTheme ? "Switch to light mode" : "Switch to dark mode";

  return (
    <motion.nav
      className={`nav${scrolled ? " nav--scrolled" : ""}`}
      initial={{ y: -72, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
    >
      <div className="nav__inner">
        <a href="#home" className="nav__brand" onClick={(e) => scrollTo(e, "home")} aria-label="Usama Sajid — home">
          {brand}
        </a>

        <ul className="nav__links">
          {LINKS.map(({ id, label }) => (
            <li key={id}>
              <a
                href={`#${id}`}
                className={`nav__link${active === id ? " is-active" : ""}`}
                onClick={(e) => scrollTo(e, id)}
              >
                {label}
                {active === id && (
                  <motion.span
                    className="nav__active"
                    layoutId="nav-active"
                    transition={{ duration: 0.34, ease: [0.22, 1, 0.36, 1] }}
                  />
                )}
              </a>
            </li>
          ))}
        </ul>

        <div className="nav__controls">
          <button
            type="button"
            className="nav__theme-toggle"
            onClick={onToggleTheme}
            aria-label={themeToggleLabel}
            aria-pressed={isDarkTheme}
            title={themeToggleLabel}
          >
            <AnimatePresence mode="wait" initial={false}>
              <motion.span
                key={theme}
                initial={{ opacity: 0, rotate: -35, scale: 0.72 }}
                animate={{ opacity: 1, rotate: 0, scale: 1 }}
                exit={{ opacity: 0, rotate: 35, scale: 0.72 }}
                transition={{ duration: 0.18, ease: [0.22, 1, 0.36, 1] }}
                aria-hidden="true"
              >
                {isDarkTheme ? <FiSun /> : <FiMoon />}
              </motion.span>
            </AnimatePresence>
          </button>

          {cvUrl && (
            <div className="nav__actions">
              <a href={cvUrl} download target="_blank" rel="noreferrer" className="btn btn--dark nav__cta">
                Resume
                <HiDownload />
              </a>
            </div>
          )}

          <button className="nav__burger" onClick={() => setToggle(true)} aria-label="Open menu">
            <HiMenuAlt4 />
          </button>
        </div>
      </div>

      <motion.span className="nav__progress" style={{ scaleX: progress }} aria-hidden="true" />

      {createPortal(
        <AnimatePresence>
          {toggle && (
            <>
              <motion.div
                className="nav__backdrop"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.24 }}
                onClick={() => setToggle(false)}
              />
              <motion.div
                className="nav__drawer"
                role="dialog"
                aria-modal="true"
                aria-label="Site menu"
                initial={{ x: "104%" }}
                animate={{ x: 0 }}
                exit={{ x: "104%" }}
                transition={{ duration: 0.42, ease: [0.22, 1, 0.36, 1] }}
              >
                <div className="nav__drawer-head">
                  <span className="nav__drawer-index">Menu</span>
                  <button className="nav__close" onClick={() => setToggle(false)} aria-label="Close menu">
                    <HiX />
                  </button>
                </div>

                <ul className="nav__drawer-links">
                  {LINKS.map(({ id, label }, index) => (
                    <motion.li
                      key={id}
                      initial={{ opacity: 0, y: 26 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.08 + index * 0.055, duration: 0.44, ease: [0.22, 1, 0.36, 1] }}
                    >
                      <a
                        href={`#${id}`}
                        className={active === id ? "is-active" : ""}
                        onClick={(e) => {
                          scrollTo(e, id);
                          setToggle(false);
                        }}
                      >
                        <em>{String(index + 1).padStart(2, "0")}</em>
                        <span>{label}</span>
                        <FiArrowUpRight aria-hidden="true" />
                      </a>
                    </motion.li>
                  ))}
                </ul>

                <motion.div
                  className="nav__drawer-foot"
                  initial={{ opacity: 0, y: 18 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.42, duration: 0.4 }}
                >
                  <a href="mailto:usamasajid8@gmail.com" className="nav__drawer-mail">
                    usamasajid8@gmail.com
                  </a>
                  {cvUrl && (
                    <a href={cvUrl} download target="_blank" rel="noreferrer" className="btn btn--dark">
                      Download Resume
                      <HiDownload />
                    </a>
                  )}
                </motion.div>
              </motion.div>
            </>
          )}
        </AnimatePresence>,
        document.body
      )}
    </motion.nav>
  );
};

export default Navbar;
